/* =========================================================================
   ClueVine — seeds
   Daily seeds are derived from the calendar date so everyone shares a puzzle.
   Endless seeds take a caller-supplied salt so they're unique but still
   reproducible. `pickAvoiding` is the anti-frustration primitive that keeps
   recently-seen answers from coming back too soon.
   ========================================================================= */

import { createPrng, hashSeed } from './prng';
import type { PlayMode, Prng } from './types';

/** First ClueVine day (local calendar date); daily puzzle numbers count up from here. */
const EPOCH = { y: 2026, m: 0, d: 1 } as const;
const DAY_MS = 86_400_000;

export interface SeedInfo {
  /** The full seed string fed to the PRNG. */
  seed: string;
  /** A short shareable code, e.g. "Z3F9K2A". */
  label: string;
  playMode: PlayMode;
  /** For daily puzzles: the running day number (#1, #2, …). */
  dayNumber?: number;
}

/** Local-time YYYY-MM-DD — the player's "today". Daily resets at local midnight. */
export function todayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The running daily number (#1 = the epoch). Computed from the local calendar
 * date via Date.UTC so DST transitions can't make two days share a number or
 * skip one — subtracting local-midnight timestamps would be off by an hour for
 * half the year.
 */
export function dayNumber(date: Date = new Date()): number {
  const utc = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((utc - Date.UTC(EPOCH.y, EPOCH.m, EPOCH.d)) / DAY_MS) + 1;
}

/** The local calendar date of a daily number — the inverse of dayNumber(). */
export function dateForDay(day: number): Date {
  return new Date(EPOCH.y, EPOCH.m, EPOCH.d + day - 1);
}

/** Timestamp of the next local midnight — when the next daily unlocks. */
export function nextDailyAt(now: Date = new Date()): number {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
}

/**
 * The pre-1.1 day numbering (floor of a local-midnight difference), which drifted
 * by a day during daylight-saving time. Kept only to migrate stored day numbers.
 */
export function legacyDayNumber(date: Date): number {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const epoch = new Date(EPOCH.y, EPOCH.m, EPOCH.d).getTime();
  return Math.floor((start - epoch) / DAY_MS) + 1;
}

/** Map a day number stored under the legacy scheme onto the current one. */
export function migrateLegacyDay(legacy: number): number {
  // The legacy number is at most one behind the true one, so look a little either side
  // and take the latest calendar date that carried it.
  for (let offset = 1; offset >= -2; offset--) {
    const date = dateForDay(legacy + offset);
    if (legacyDayNumber(date) === legacy) return dayNumber(date);
  }
  return legacy;
}

function shortCode(input: string): string {
  return hashSeed(input).toString(36).toUpperCase().padStart(7, '0').slice(0, 7);
}

export function dailySeed(modeId: string, date: Date = new Date()): SeedInfo {
  const key = todayKey(date);
  return {
    seed: `cluevine:${modeId}:daily:${key}`,
    label: shortCode(`${modeId}:${key}`),
    playMode: 'daily',
    dayNumber: dayNumber(date),
  };
}

export function endlessSeed(modeId: string, salt: string | number): SeedInfo {
  const s = String(salt);
  return {
    seed: `cluevine:${modeId}:endless:${s}`,
    label: shortCode(`${modeId}:e:${s}`),
    playMode: 'endless',
  };
}

/** A fresh PRNG for a given seed. */
export function seedPrng(seed: string | SeedInfo): Prng {
  return createPrng(typeof seed === 'string' ? seed : seed.seed);
}

/**
 * Deterministically pick a candidate while avoiding a recent set. Falls back to
 * the full pool if every candidate has been seen recently (so it never fails).
 */
export function pickAvoiding(
  prng: Prng,
  candidates: readonly string[],
  avoid: ReadonlySet<string>,
): string {
  const fresh = candidates.filter((w) => !avoid.has(w.toLowerCase()));
  const pool = fresh.length > 0 ? fresh : candidates;
  return prng.pick(pool);
}
